import { observer } from "mobx-react";
import React from "react";

export type ImageGenModel = 'SDXL' | 'Flux1.Dev' | 'Flux.Kontext' | 'Flux1.Schnell' | 'Flux2.Dev' | 'Flux2.Klein' | 'zImage' | 'qwen';
type ImageGenModelType = 'SDXL' | 'Flux' | 'zImage' | 'Qwen';

type ImageModelPickerProps = {
  model: ImageGenModel;
  onSelect: (model: ImageGenModel) => void;
  hasImages?: boolean;
};

const DropdownMenu = ({ 
  models, 
  currentModel, 
  onSelect, 
  getDisplayName,
  disabledModels
}: { 
  models: ImageGenModel[], 
  currentModel: ImageGenModel, 
  onSelect: (model: ImageGenModel) => void,
  getDisplayName: (model: ImageGenModel) => string,
  disabledModels?: Set<ImageGenModel>
}) => {
  return (
    <div className="dropdown-menu">
      {models.map((m) => {
        const isDisabled = disabledModels?.has(m);
        return (
          <div
            key={m}
            className={`dropdown-item${currentModel === m ? ' active' : ''}${isDisabled ? ' disabled' : ''}`}
            onClick={isDisabled ? undefined : () => onSelect(m)}
          >
            <span className="dropdown-item-text">{getDisplayName(m)}</span>
            {currentModel === m && <span className="dropdown-check">✓</span>}
          </div>
        );
      })}
    </div>
  );
};

@observer
class ImageModelPicker extends React.Component<ImageModelPickerProps> {
  state = {
    openDropdown: null as string | null
  };

  dropdownRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target as Node)) {
      this.setState({ openDropdown: null });
    }
  };

  getFluxModels(): ImageGenModel[] {
    return [
      'Flux1.Dev',
      'Flux.Kontext',
      'Flux2.Dev',
      'Flux2.Klein',
    ];
  }

  isFluxModel(model: ImageGenModel): boolean {
    return this.getFluxModels().includes(model);
  }

  getDisplayName = (model: ImageGenModel): string => {
    const mapping: Record<string, string> = {
      'Flux1.Dev': 'Flux.1 Dev',
      'Flux.Kontext': 'Flux Kontext',
      'Flux1.Schnell': 'Flux.1 Schnell',
      'Flux2.Dev': 'Flux.2 Dev',
      'Flux2.Klein': 'Flux.2 Klein',
      'qwen': 'Qwen',
    };
    return mapping[model] || model;
  };

  toggleDropdown = (modelName: string) => {
    this.setState({ 
      openDropdown: this.state.openDropdown === modelName ? null : modelName 
    });
  };

  render() {
    const { model, onSelect } = this.props;
    const { openDropdown } = this.state;
    const hasImages = !!this.props.hasImages;
    
    const models: { name: ImageGenModelType, displayName?: string, previewUrl: string }[] = [
      { name: 'zImage', previewUrl: '/img/models/zimage-preview.jpg' },
      { name: 'SDXL', previewUrl: '/img/models/sdxl-preview.jpg' },
      { name: 'Flux', previewUrl: '/img/models/flux-preview.jpg' },
      { name: 'Qwen', previewUrl: '/img/models/qwen-image-edit-preview.jpg' }
    ];

    return (
      <>
        <div className="image-gen-controls-model" ref={this.dropdownRef}>
          <div className="control-group">
            <label>Model:</label>
            <div className="image-model-selector">
              {models.map((m) => {
                const isFluxActive = m.name === 'Flux' && this.isFluxModel(model);
                const isQwenActive = m.name === 'Qwen' && model === 'qwen';
                const isActive = model === m.name || isFluxActive || isQwenActive;
                const isDropdownOpen = openDropdown === m.name;
                const currentSelection = isFluxActive ? this.getDisplayName(model) : '';
                const isFlux = m.name === 'Flux';
                // Disable t2i-only models when images are attached
                const isDisabled = hasImages && (m.name === 'zImage' || m.name === 'SDXL');
                
                return (
                  <div key={m.name} className="model-btn-wrapper">
                    <button
                      className={`model-btn${isActive ? ' active' : ''}${isDropdownOpen ? ' dropdown-open' : ''}${isDisabled ? ' disabled' : ''}`}
                      disabled={isDisabled}
                      onClick={() => {
                        if (isDisabled) return;
                        if (isFlux) {
                          // Clicking image area selects Flux2.Klein as default
                          this.setState({ openDropdown: null });
                          onSelect('Flux2.Klein');
                        } else if (m.name === 'Qwen') {
                          this.setState({ openDropdown: null });
                          onSelect('qwen');
                        } else {
                          this.setState({ openDropdown: null });
                          onSelect(m.name as ImageGenModel);
                        }
                      }}
                      title={isDisabled ? `${m.name} does not support image-to-image` : m.name}
                    >
                      <img src={m.previewUrl} alt={m.name} className="model-preview" />
                      <div
                        className="model-name-row"
                        onClick={isFlux ? (e) => { e.stopPropagation(); this.toggleDropdown('Flux'); } : undefined}
                      >
                        <span>{m.displayName || m.name}</span>
                        {isFlux && <span className="dropdown-icon">▼</span>}
                      </div>
                      {isFlux && isActive && currentSelection && (
                        <div className="selected-model">{currentSelection}</div>
                      )}
                    </button>
                    
                    {isFlux && isDropdownOpen && (
                      <DropdownMenu
                        models={this.getFluxModels()}
                        currentModel={model}
                        onSelect={(selectedModel) => {
                          onSelect(selectedModel);
                          this.setState({ openDropdown: null });
                        }}
                        getDisplayName={this.getDisplayName}
                        disabledModels={hasImages ? new Set<ImageGenModel>(['Flux1.Dev']) : undefined}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default ImageModelPicker;